import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { BrandHeader } from "@/components/quote/BrandHeader";
import { ServiceStep } from "@/components/quote/ServiceStep";
import { PhotoStep } from "@/components/quote/PhotoStep";
import { PreviewStep } from "@/components/quote/PreviewStep";
import { PreviewResult } from "@/components/quote/PreviewResult";
import { DescriptionStep } from "@/components/quote/DescriptionStep";
import { SizeStep } from "@/components/quote/SizeStep";
import { TimelineStep } from "@/components/quote/TimelineStep";
import { BudgetStep } from "@/components/quote/BudgetStep";
import { ContactStep, contactErrors } from "@/components/quote/ContactStep";
import { ThankYou, Unavailable } from "@/components/quote/FinalScreens";
import { usePhotoUploads } from "@/components/quote/usePhotoUploads";
import { LegalLinks } from "@/components/LegalLinks";

const STEP_META = {
  service: { name: "Service", title: "What type of project is it?", sub: "Pick the one that fits best." },
  photos: { name: "Photos", title: "Show us the space", sub: "Add 1 to 8 photos from your camera or gallery." },
  preview: { name: "Preview", title: "Want to see what it could look like?", sub: "Get a free AI preview of your finished space. Optional." },
  details: { name: "Details", title: "What would you like done?", sub: "A few sentences is perfect." },
  size: { name: "Size", title: "About how big is the job?", sub: "A rough idea is fine." },
  timeline: { name: "Timeline", title: "When would you like to start?", sub: "" },
  budget: { name: "Budget", title: "What's your budget?", sub: "Optional. It helps us suggest the right options." },
  contact: { name: "Contact", title: "Where can we reach you?", sub: "We'll use this to follow up about your project." },
};

const EMPTY_CONTACT = { name: "", phone: "", email: "", city: "", zip: "" };
const EMPTY_PREVIEW = { want: null, photo_path: null, style: null, preferences: {} };

export default function QuoteForm() {
  const { slug } = useParams();
  const [contractor, setContractor] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState({ service_type: null, description: "", size: null, sqft: "", timeline: null, budget: null, contact: EMPTY_CONTACT });
  const [preview, setPreview] = useState(EMPTY_PREVIEW);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const uploads = usePhotoUploads(slug);

  useEffect(() => {
    api.get(`/public/contractors/${slug}`).then(({ data }) => setContractor(data)).catch(() => setLoadError(true));
  }, [slug]);

  if (loadError || (contractor && !contractor.available)) return <Unavailable />;
  if (!contractor) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const previewPrefs = contractor.preview?.enabled ? contractor.preview.services[form.service_type] : undefined;
  const keys = ["service", "photos", ...(previewPrefs ? ["preview"] : []), "details", "size", "timeline", "budget", "contact"];
  const key = keys[step];
  const photoPaths = uploads.photos.map((p) => p.path);
  const previewValid = preview.want !== true || (photoPaths.includes(preview.photo_path) && !!preview.style);
  const canNext = {
    service: !!form.service_type, photos: uploads.ready, preview: previewValid, details: form.description.trim().length >= 3,
    size: !!form.size, timeline: !!form.timeline, budget: true, contact: true,
  }[key];
  const isLast = step === keys.length - 1;

  const go = (d) => {
    if (key === "preview" && d > 0 && preview.want !== true) setPreview((p) => ({ ...p, want: false }));
    setDir(d); setStep((s) => s + d); window.scrollTo({ top: 0 });
  };
  const wantsPreview = !!previewPrefs && preview.want === true && previewValid;

  const submit = async () => {
    if (Object.values(contactErrors(form.contact)).some(Boolean)) { setShowErrors(true); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/contractors/${slug}/leads`, {
        ...form,
        sqft: form.sqft ? parseInt(form.sqft, 10) : null,
        photo_paths: photoPaths,
        contact: { ...form.contact, name: form.contact.name.trim(), email: form.contact.email.trim(), city: form.contact.city.trim() },
        preview: wantsPreview ? { photo_path: preview.photo_path, style: preview.style, preferences: preview.preferences } : null,
      });
      setDone({ token: data.preview_token, before: uploads.photos.find((p) => p.path === preview.photo_path)?.preview });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const body = {
    service: <ServiceStep services={contractor.services} value={form.service_type} onChange={(v) => { set("service_type")(v); setPreview(EMPTY_PREVIEW); setTimeout(() => go(1), 180); }} />,
    photos: <PhotoStep photos={uploads.photos} onAdd={uploads.add} onRemove={uploads.remove} />,
    preview: <PreviewStep value={preview} onChange={setPreview} photos={uploads.photos} styles={contractor.preview?.styles || []} prefs={previewPrefs || []} />,
    details: <DescriptionStep value={form.description} onChange={set("description")} />,
    size: <SizeStep size={form.size} sqft={form.sqft} onSize={set("size")} onSqft={set("sqft")} />,
    timeline: <TimelineStep value={form.timeline} onChange={(v) => { set("timeline")(v); setTimeout(() => go(1), 180); }} />,
    budget: <BudgetStep value={form.budget} onChange={set("budget")} />,
    contact: <ContactStep value={form.contact} onChange={set("contact")} showErrors={showErrors} />,
  }[key];

  const summary = [
    ["Service", form.service_type], ["Photos", `${uploads.photos.length} uploaded`],
    ["Size", form.size + (form.sqft ? ` · ${form.sqft} sq ft` : "")], ["Timeline", form.timeline],
    ["Budget", form.budget || "Not provided"], ["Location", `${form.contact.city} ${form.contact.zip}`],
  ];
  const nextLabel = isLast ? (submitting ? "Sending..." : "Send my project")
    : (key === "budget" && !form.budget) || (key === "preview" && preview.want !== true) ? "Skip"
    : key === "photos" && uploads.uploading ? "Uploading..." : "Continue";

  return (
    <div className="min-h-screen bg-slate-50" style={{ "--brand": contractor.brand_color }} data-testid="public-quote-form-container">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-xl px-5 py-5"><BrandHeader contractor={contractor} /></div>
      </div>
      <div className="mx-auto flex max-w-xl flex-col px-5">
        {done ? (
          <ThankYou companyName={contractor.company_name} summary={summary}
            preview={done.token && <PreviewResult token={done.token} beforeSrc={done.before} />} />
        ) : (
          <>
            <div className="pt-6">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-slate-900" data-testid="form-step-name">{STEP_META[key].name}</span>
                <span className="text-slate-500" data-testid="form-step-counter">Step {step + 1} of {keys.length}</span>
              </div>
              <div className="mt-2.5 flex gap-1" data-testid="form-step-progress-bar">
                {keys.map((k, i) => (
                  <div key={k} className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
                    <motion.div className="h-full rounded-full bg-[color:var(--brand)]" initial={false} animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.3 }} />
                  </div>
                ))}
              </div>
            </div>
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div key={key} initial={{ opacity: 0, x: 20 * dir }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 * dir }}
                transition={{ duration: 0.2 }} className="flex-1 pb-36 pt-7">
                <h1 className="text-[26px] font-bold leading-tight tracking-tight text-slate-900 sm:text-3xl" data-testid="form-step-title">{STEP_META[key].title}</h1>
                {STEP_META[key].sub && <p className="mt-1.5 text-base text-slate-500">{STEP_META[key].sub}</p>}
                <div className="mt-6">{body}</div>
                <LegalLinks newTab className="mt-10" />
              </motion.div>
            </AnimatePresence>
            <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/90 backdrop-blur-xl" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
              <div className="mx-auto flex max-w-xl gap-3 px-5 py-3.5">
                {step > 0 && (
                  <button type="button" onClick={() => go(-1)} data-testid="form-back-button"
                    className="flex h-14 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-base font-semibold text-slate-700 transition-transform active:scale-95">
                    <ArrowLeft className="h-5 w-5" /> Back
                  </button>
                )}
                <button type="button" disabled={!canNext || submitting} onClick={isLast ? submit : () => go(1)}
                  data-testid={isLast ? "form-submit-button" : "form-next-button"}
                  className="flex h-14 flex-1 items-center justify-center gap-2 rounded-xl bg-[color:var(--brand)] text-base font-semibold text-white shadow-[0_8px_20px_-10px_var(--brand)] transition-[opacity,transform] active:scale-[0.98] disabled:opacity-40 disabled:shadow-none">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  {nextLabel}
                  {!isLast && !submitting && <ArrowRight className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
